package gr.tech.GRAttestation.components.modals

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.disabled
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import gr.tech.GRAttestation.components.inputs.EmailInput
import gr.tech.GRAttestation.components.inputs.StringTextArea
import gr.tech.GRAttestation.components.inputs.ValidationTrigger
import gr.tech.GRAttestation.components.widgets.AlertManager
import gr.tech.GRAttestation.components.widgets.UserAlert
import gr.tech.GRAttestation.infrastructure.api.adminCreateNewStoreUser
import gr.tech.GRAttestation.infrastructure.api.adminSendStoreUserInviteEmail
import gr.tech.GRAttestation.models.storeusers.StoreAddUsersRequest
import gr.tech.GRAttestation.styles.bsInputLabelStyle
import gr.tech.GRAttestation.styles.bsInputStyle
import gr.tech.GRAttestation.util.ROUTES
import gr.tech.models.storeusers.StoreUsersInviteDTO
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private enum class InviteTab { EMAIL, LINK }

@Composable
fun StoreUserAddModal(
    lockedStoreId: Int? = null,
    alertManager: AlertManager,
    onClose: () -> Unit,
    onInviteCreated: () -> Unit,
) {
    val scope = rememberCoroutineScope()

    var activeTab by remember { mutableStateOf(InviteTab.LINK) }
    var sendToEmail by remember { mutableStateOf("") }
    var notes by remember { mutableStateOf("") }
    var selectedStoreId by remember { mutableStateOf<Int?>(lockedStoreId) }
    var generatedLink by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(false) }
    var localError by remember { mutableStateOf("") }

    fun validateBase(): Boolean {
        if (lockedStoreId == null && selectedStoreId == null) {
            localError = "Please select a store."
            return false
        }
        return true
    }

    fun validateEmail(): Boolean {
        if (sendToEmail.length < 5 || !sendToEmail.contains("@")) {
            localError = "Enter a valid email address."
            return false
        }
        return true
    }

    fun handleGenerateLink() {
        localError = ""
        if (!validateBase()) return
        val storeId = lockedStoreId ?: selectedStoreId!!
        loading = true
        scope.launch {
            var createError = ""
            var createdInvite: gr.tech.models.storeusers.StoreUsersInviteDTO? = null
            adminCreateNewStoreUser(
                newStoreUser = StoreAddUsersRequest(email = null, storeId = storeId, notes = notes),
                onSuccess = { result ->
                    if (result.success && result.data != null) createdInvite = result.data
                    else createError = result.error?.message ?: "Error creating invite."
                },
                onError = { createError = "Network error creating invite." }
            )
            if (createError.isNotBlank() || createdInvite == null) {
                localError = createError.ifBlank { "Error creating invite." }
                loading = false
                return@launch
            }
            generatedLink = "${ROUTES.FRONTEND_URL}/finish-registration?token=${createdInvite!!.token}"
            loading = false
        }
    }

    fun handleSendEmail() {
        localError = ""
        if (!validateBase() || !validateEmail()) return
        val storeId = lockedStoreId ?: selectedStoreId!!
        loading = true
        scope.launch {
            var createError = ""
            var createdInvite: StoreUsersInviteDTO? = null

            adminCreateNewStoreUser(
                newStoreUser = StoreAddUsersRequest(email = sendToEmail.trim().lowercase().ifBlank { null }, storeId = storeId, notes = notes),
                onSuccess = { result ->
                    if (result.success && result.data != null) createdInvite = result.data
                    else createError = result.error?.message ?: "Error creating invite."
                },
                onError = { createError = "Network error creating invite." }
            )
            if (createError.isNotBlank() || createdInvite == null) {
                localError = createError.ifBlank { "Error creating invite." }
                loading = false
                return@launch
            }

            var sendError = ""
            adminSendStoreUserInviteEmail(
                storeId = storeId,
                inviteUuid = createdInvite.uuid,
                sendToEmail = sendToEmail.trim().lowercase(),
                onSuccess = { result ->
                    if (!result.success) sendError = result.error?.message ?: "Error sending email."
                },
                onError = { sendError = "Network error sending email." }
            )

            if (sendError.isNotBlank()) {
                localError = sendError
                loading = false
                return@launch
            }
            alertManager.addAlert(UserAlert(message = "Invite email sent!", status = 2))
            onInviteCreated()
            onClose()
        }
    }

    Div(attrs = Modifier
        .classNames("card", "d-flex", "flex-column")
        .maxWidth(600.px)
        .fillMaxWidth(90.percent)
        .toAttrs {
            onClick { it.stopPropagation() }
        }
    ) {
        // Header
        Div(attrs = Modifier.classNames("d-flex", "flex-row", "justify-content-between", "p-3", "pb-3").toAttrs()) {
            H5(Modifier.classNames("fw-bold", "m-0").toAttrs()) {
                Text("New Store User Invite")
            }
            Button(Modifier.classNames("btn-close").toAttrs {
                onClick { onClose() }
            })
        }

        Div(attrs = Modifier
            .classNames("px-3", "pb-3", "d-flex", "flex-column", "gap-3")
            .maxHeight(70.vh)
            .overflow(Overflow.Hidden, Overflow.Auto)
            .toAttrs()
        ) {
            // Store Selection
            StorePickerSection(
                lockedStoreId = lockedStoreId,
                selectedStoreId = selectedStoreId,
                onStoreSelected = { selectedStoreId = it }
            )

            // Notes
            StringTextArea(
                direction = FlexDirection.Column,
                justifyContent = JustifyContent.Start,
                alignItems = AlignItems.Start,
                labelModifier = Modifier.bsInputLabelStyle(),
                textAreaModifier = Modifier.bsInputStyle(),
                inputId = "StoreUserNotes",
                labelText = "Extra Notes",
                value = notes,
                minLength = 0,
                maxLength = 512,
                isRequired = false,
                validationTrigger = ValidationTrigger.OnChange,
                onValueChange = { notes = it.trim() }
            )

            // Tabs
            Ul(attrs = Modifier.classNames("nav", "nav-tabs").toAttrs()) {
                Li(attrs = Modifier.classNames("nav-item").toAttrs()) {
                    Button(attrs = Modifier
                        .classNames("nav-link", if (activeTab == InviteTab.LINK) "active" else "nonactive")
                        .onClick { activeTab = InviteTab.LINK; localError = "" }
                        .toAttrs()
                    ) { Text("Link") }
                }
                Li(attrs = Modifier.classNames("nav-item").toAttrs()) {
                    Button(attrs = Modifier
                        .classNames("nav-link", if (activeTab == InviteTab.EMAIL) "active" else "nonactive")
                        .onClick { activeTab = InviteTab.EMAIL; localError = "" }
                        .toAttrs()
                    ) { Text("Email") }
                }
            }

            // Tab Content
            Div(attrs = Modifier.classNames("pt-2", "pb-4").toAttrs()) {

                // Link Tab
                if (activeTab == InviteTab.LINK) {
                    P(attrs = Modifier.classNames("form-text").toAttrs()) {
                        Text("Generate a registration link to copy and share manually.")
                    }
                    Div(attrs = Modifier.classNames("input-group").toAttrs()) {
                        Input(
                            type = org.jetbrains.compose.web.attributes.InputType.Text,
                            attrs = Modifier.classNames("form-control", "disabled").toAttrs {
                                disabled(true)
                                attr("value", generatedLink)
                                attr("readonly", "")
                                attr("placeholder", "Click 'Generate Link' to create a link...")
                            }
                        )
                        if (generatedLink.isNotBlank()) {
                            Button(attrs = Modifier
                                .classNames("btn", "btn-outline-secondary")
                                .onClick {
                                    val link = generatedLink
                                    js("navigator.clipboard.writeText(link)")
                                    alertManager.addAlert(UserAlert(message = "Link copied!", status = 2))
                                }
                                .toAttrs()
                            ) { Text("Copy") }
                        }
                    }
                }

                // Email Tab
                if (activeTab == InviteTab.EMAIL) {
                    P(attrs = Modifier.classNames("form-text").toAttrs()) {
                        Text("Send the registration link directly to an email address.")
                    }
                    EmailInput(
                        direction = FlexDirection.Column,
                        justifyContent = JustifyContent.Start,
                        alignItems = AlignItems.Start,
                        labelModifier = Modifier.bsInputLabelStyle(),
                        inputModifier = Modifier.bsInputStyle(),
                        inputId = "StoreUserSendToEmail",
                        labelText = "Send To Email",
                        value = sendToEmail,
                        showError = false,
                        minLength = 5,
                        maxLength = 128,
                        isRequired = true,
                        placeholder = "recipient@example.com",
                        validationTrigger = ValidationTrigger.OnChange,
                        onValueChange = { sendToEmail = it.trim().lowercase() }
                    )
                }
            }

            // Error
            if (localError.isNotBlank()) {
                Div(attrs = Modifier.classNames("alert", "alert-danger", "py-2").toAttrs()) {
                    Text(localError)
                }
            }

            // Action Buttons
            Div(attrs = Modifier.classNames("w-100", "mt-2", "gap-2", "d-flex", "flex-row", "justify-content-between", "align-items-center").toAttrs()) {
                Button(
                    attrs = Modifier.classNames("btn", "btn-secondary")
                        .toAttrs { onClick { onClose() } }
                ) {
                    Text("Cancel")
                }

                if (activeTab == InviteTab.LINK) {
                    if (generatedLink.isNotBlank()) {
                        Button(
                            attrs = Modifier
                                .classNames("btn", "btn-success")
                                .toAttrs { onClick { onInviteCreated(); onClose() } }
                        ) {
                            Text("Done")
                        }
                    } else {
                        Button(
                            attrs = Modifier
                                .classNames("btn", "btn-primary")
                                .toAttrs {
                                    if (loading) attr("disabled", "")
                                    onClick { if (!loading) handleGenerateLink() }
                                }
                        ) {
                            Text(if (loading) "Generating..." else "Generate Link")
                        }
                    }
                } else {
                    Button(
                        attrs = Modifier
                            .classNames("btn", "btn-primary")
                            .toAttrs {
                                if (loading) attr("disabled", "")
                                onClick { if (!loading) handleSendEmail() }
                            }
                    ) {
                        Text(if (loading) "Sending..." else "Send Invite")
                    }
                }
            }
        }
    }
}
